import { Component, Inject, OnInit, OnDestroy, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA, ElementRef, ViewChild, inject, HostListener, Renderer2 } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';

import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatNativeDateModule } from '@angular/material/core';
import { MatAutocompleteModule, MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TooltipPosition, MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { SHARED_IMPORTS } from '../shared/shared-imports';
import { ContentService } from '../../services/content.service';
import { OrderByPipe } from '../../pipes/order-by.pipe';
import { saveAs } from 'file-saver';


// Chart.js imports
import {
	Chart,
	registerables,
	TimeScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	Filler
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';



// Register Chart.js components (without the background plugin - we'll add it locally)
Chart.register(
	...registerables,
	TimeScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	Filler,
	zoomPlugin,
	annotationPlugin
);


interface ExportToggleConfig {
	field: 'IncludeTemperature' | 'IncludeHumidity' | 'IncludeAirChange' | 'IncludeAirFlowDiff';
	labelKey: string;
	cssClass?: string;
}


@Component({
	selector: 'export-measurement-component',
	standalone: true,
	templateUrl: 'components/export-measurement/export-measurement.component.html',
	styleUrls: ['components/export-measurement/export-measurement.component.css'],
	imports: [
		...SHARED_IMPORTS,
		MatIconModule,
		MatDatepickerModule,
		MatInputModule,
		MatFormFieldModule,
		MatNativeDateModule,
		MatAutocompleteModule,
		MatCheckboxModule,
		MatTooltipModule,
		MatButtonModule,
		OrderByPipe,
		ReactiveFormsModule,
		MatSelectModule
	],
	schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class ExportMeasurementComponent implements OnInit, OnDestroy {

	private router = inject(Router);
	private http = inject(HttpClient);
	private content = inject(ContentService);
	private elementRef = inject(ElementRef);
	private renderer = inject(Renderer2);

	@ViewChild('unitAuto') unitAuto!: MatAutocomplete;
	@ViewChild('positionAuto') positionAuto!: MatAutocomplete;
	@ViewChild('unitAutoTrigger') unitAutoTrigger!: MatAutocompleteTrigger;
	@ViewChild('unitInput') unitInput!: ElementRef<HTMLInputElement>;
	@ViewChild('positionAutoTrigger') positionAutoTrigger!: MatAutocompleteTrigger;
	@ViewChild('positionInput') positionInput!: ElementRef<HTMLInputElement>;


	public exportToggles: ExportToggleConfig[] = [
		{ field: 'IncludeTemperature', labelKey: 'ExportMeasurement.Temperature', cssClass: 'temp' },
		{ field: 'IncludeHumidity', labelKey: 'ExportMeasurement.Humidity' },
		{ field: 'IncludeAirChange', labelKey: 'ExportMeasurement.AirChange' },
		{ field: 'IncludeAirFlowDiff', labelKey: 'ExportMeasurement.AirFlowDiff', cssClass: 'airFl' }
	];

	chartSections = [
		{
			id: 'temperatureChart',
			labelKey: 'ExportMeasurement.Temperature',
			condition: () => this.showTemperaturePlot && this.ExportRequestModel.IncludeTemperature
		},
		{
			id: 'humidityChart',
			labelKey: 'ExportMeasurement.Humidity',
			condition: () => this.showHumidityPlot && this.ExportRequestModel.IncludeHumidity
		},
		{
			id: 'airChangeChart',
			labelKey: 'ExportMeasurement.AirChange',
			condition: () => this.showAirChangePlot && this.ExportRequestModel.IncludeAirChange
		},
		{
			id: 'airFlowDiffChart',
			labelKey: 'ExportMeasurement.AirFlowDiff',
			condition: () => this.showAirFlowDiffPlot && this.ExportRequestModel.IncludeAirFlowDiff
		}
	];


	public siteLevelActive = false;
	public units: any[] = [];
	public locations: any[] = [];
	public filteredLocations: any[] = [];
	public filteredLocationsTemp: any[] = [];
	public sites: any[] = [];
	public selectedSite: any;
	public selectedUnit: any;
	public selectedPosition: any;
	public searchLoc = "";
	public searchText = "";
	public selectedSiteDisplay: any = null;

	// Track active measurement type for vanilla JS replacement
	public activeMeasurementType: number = 1;

	public showTemperaturePlot = false;
	public showHumidityPlot = false;
	public showAirChangePlot = false;
	public showAirFlowDiffPlot = false;
	public isDisabledUnit = false;
	public isDisabledPosition = true;
	public isDisabledSite = true;

	public myDate = new Date();
	public startDate = new Date();
	public endDate = new Date();
	public maxDateEnd = new Date(
		this.myDate.getFullYear(),
		this.myDate.getMonth(),
		this.myDate.getDate()
	);
	public time = new Date(
		this.myDate.getFullYear(),
		this.myDate.getMonth(),
		this.myDate.getDate());

	public ExportRequestModel = {
		IncludeHumidity: true,
		IncludeTemperature: true,
		IncludeAirFlowDiff: true,
		IncludeAirChange: true,
		UnitId: null,
		PositionId: null,
		StartDate: '',
		EndDate: '',
		MeasurementType: 1
	};


	public justLoaded = {
		IncludeHumidity: true,
		IncludeTemperature: true,
		IncludeAirFlowDiff: true,
		IncludeAirChange: true
	}

	private tempData: any = null;
	private humidityData: any = null;
	private airChangeData: any = null;
	private airFlowDiffData: any = null;

	private tickOptions: string = '';
	private tickInterval: string = '';


	public temperatureChart: Chart | null = null;
	public humidityChart: Chart | null = null;
	public airChangeChart: Chart | null = null;
	public airFlowDiffChart: Chart | null = null;

	public temperaturePlotLegend = '';
	public humidityPlotLegend = '';
	public airChangePlotLegend = '';
	public airFlowDiffPlotLegend = '';
	public plotLegendNoDataFound = '';
	public plotLegendDefault = '';
	public getContent: (key: string, ...args: any[]) => string;



	constructor(
	) { }

	ngOnInit(): void {
		this.getContent = this.content.getContent.bind(this.content);
		this.plotLegendDefault = this.content.getContent("UnitDetail.ShowingDataForTheLast12Hours");
		this.plotLegendNoDataFound = this.content.getContent("UnitDetail.NoDataFound");
		this.getFinalLocationOverview();
		this.loadUnits();
		this.setupInteractions();
	}

	ngOnDestroy(): void {
		// Clean up Chart.js instances
		if (this.temperatureChart) {
			this.temperatureChart.destroy();
			this.temperatureChart = null;
		}
		if (this.humidityChart) {
			this.humidityChart.destroy();
			this.humidityChart = null;
		}
		if (this.airChangeChart) {
			this.airChangeChart.destroy();
			this.airChangeChart = null;
		}
		if (this.airFlowDiffChart) {
			this.airFlowDiffChart.destroy();
			this.airFlowDiffChart = null;
		}
	}

	private setupInteractions() {
		setTimeout(() => {
			this.setupButtonGroups();
			this.setupVirtualRepeatContainer();
			// Tooltip functionality is now handled by Angular Material matTooltip
		});
	}

	private setupButtonGroups() {
		// Handle button group interactions with vanilla JS
		const btnGroup1 = document.querySelectorAll('.btn-group.btnGr1 > .btn');
		btnGroup1.forEach(btn => {
			btn.addEventListener('click', (event) => {
				btnGroup1.forEach(b => b.classList.remove('active'));
				(event.target as HTMLElement).classList.add('active');
			});
		});

		// btnGroup2 handling is now done via Angular click handlers and class bindings
		// Removed vanilla JS event listeners to avoid conflicts with Angular

		const btnToggle = document.querySelectorAll('.btnToggle');
		btnToggle.forEach(btn => {
			btn.addEventListener('click', (event) => {
				(event.target as HTMLElement).classList.toggle('active');
			});
		});
	}

	private setupVirtualRepeatContainer() {
		const container = document.querySelector('md-virtual-repeat-container');
		if (container) {
			container.addEventListener('mouseover', () => {
				const level4 = document.getElementsByClassName('level-4');
				for (let i = 0; i < level4.length; i++) {
					(level4[i].parentNode!.parentNode as HTMLElement).classList.remove('notClickable');
				}
				const level1 = document.getElementsByClassName('level-1');
				for (let i = 0; i < level1.length; i++) {
					(level1[i].parentNode!.parentNode as HTMLElement).classList.add('notClickable');
				}
				const level2 = document.getElementsByClassName('level-2');
				for (let i = 0; i < level2.length; i++) {
					(level2[i].parentNode!.parentNode as HTMLElement).classList.add('notClickable');
				}
				const level3 = document.getElementsByClassName('level-3');
				for (let i = 0; i < level3.length; i++) {
					if (this.siteLevelActive) {
						(level3[i].parentNode!.parentNode as HTMLElement).classList.add('notClickable');
					} else {
						(level3[i].parentNode!.parentNode as HTMLElement).classList.remove('notClickable');
					}
				}
			});
		}
	}

	// Angular HostListener for window resize
	@HostListener('window:resize', ['$event'])
	onWindowResize(event: any) {
		// Re-render charts to maintain fixed height
		setTimeout(() => {
			this.renderGraph(this.tempData, this.humidityData, this.airChangeData, this.airFlowDiffData);
		}, 100);
	}



	private loadUnits() {
		this.http.get('/api/unit/getAll').subscribe((response: any) => {
			const data = response as { Scunits: any[] };
			this.units = data.Scunits;
		});
	}

	private formatDate(date: Date): string {
		if (!date) return '';
		const pad = (num: number, size: number) => ('000000000' + num).substr(-size);
		return `${pad(date.getDate(), 2)}/${pad(date.getMonth() + 1, 2)}/${pad(date.getFullYear(), 4)}`;
	}





	private renderGraph(
		tempData: any[],
		humidityData: any[],
		airChangeData: any[],
		airFlowDiffData: any[]
	): void {
		type ChartKey = 'temperature' | 'humidity' | 'airChange' | 'airFlowDiff';

		interface ChartConfig {
			key: ChartKey;
			id: string;
			enabled: () => boolean;
			data: any[];
			chartRef: keyof ExportMeasurementComponent;
			tooltipSuffix: string;
			tooltipDecimals: number;
		}

		const charts: ChartConfig[] = [
			{
				key: 'temperature',
				id: 'temperatureChart',
				enabled: () => this.ExportRequestModel.IncludeTemperature,
				data: tempData,
				chartRef: 'temperatureChart',
				tooltipSuffix: '°C',
				tooltipDecimals: 1
			},
			{
				key: 'humidity',
				id: 'humidityChart',
				enabled: () => this.ExportRequestModel.IncludeHumidity,
				data: humidityData,
				chartRef: 'humidityChart',
				tooltipSuffix: '%',
				tooltipDecimals: 0
			},
			{
				key: 'airChange',
				id: 'airChangeChart',
				enabled: () => this.ExportRequestModel.IncludeAirChange,
				data: airChangeData,
				chartRef: 'airChangeChart',
				tooltipSuffix: '',
				tooltipDecimals: 0
			},
			{
				key: 'airFlowDiff',
				id: 'airFlowDiffChart',
				enabled: () => this.ExportRequestModel.IncludeAirFlowDiff,
				data: airFlowDiffData,
				chartRef: 'airFlowDiffChart',
				tooltipSuffix: '%',
				tooltipDecimals: 0
			}
		];

		// Use setTimeout to ensure DOM is ready
		setTimeout(() => {
		charts.forEach(chart => {
				if (chart.data && chart.enabled() && chart.data.length > 0) {
					this.createChart(chart);
				}
			});
		}, 100);
	}

	private createChart(config: any): void {
		const canvas = document.getElementById(config.id) as HTMLCanvasElement;
		if (!canvas || !canvas.getContext) {
			console.error(`Chart canvas not found: ${config.id}`);
			return;
		}

		try {
			// Destroy existing chart
			const existingChart = (this as any)[config.chartRef];
			if (existingChart) {
				existingChart.destroy();
				(this as any)[config.chartRef] = null;
			}

			// Set canvas size explicitly
			canvas.width = canvas.offsetWidth || 800;
			canvas.height = 208; // Fixed height
			
			// Clear any existing content/loading state
			const ctx = canvas.getContext('2d');
			if (ctx) {
				ctx.clearRect(0, 0, canvas.width, canvas.height);
			}

			// Convert data format for Chart.js and apply data decimation for smoother appearance
			let convertedData = config.data.map(([timestamp, value]: [string, number]) => ({
				x: new Date(timestamp),
				y: value
			}));

			// Keep most data points for natural variation (only decimate if extremely dense)
			if (convertedData.length > 1000) {
				const decimationFactor = Math.max(1, Math.floor(convertedData.length / 800));
				convertedData = convertedData.filter((_, index) => index % decimationFactor === 0);
			}

			// Create Chart.js instance
			const chart = new Chart(canvas, {
				type: 'line',
				data: {
					datasets: [{
						label: config.key,
						data: convertedData as any,
						borderColor: 'rgba(75, 179, 196, 1)',
						backgroundColor: 'transparent',
						borderWidth: 1,
						pointRadius: 0,
						pointHoverRadius: 0,
						spanGaps: false, // show natural breaks in data
						tension: 0 // no smoothing - keep original data character
					}]
				},
				plugins: [{
					id: 'plotAreaBackground',
					beforeDraw: (chart: any) => {
						const ctx = chart.ctx;
						const chartArea = chart.chartArea;
						
						if (chartArea) {
							ctx.save();
							ctx.fillStyle = 'rgba(255, 253, 246, 1)';
							ctx.fillRect(chartArea.left, chartArea.top, chartArea.right - chartArea.left, chartArea.bottom - chartArea.top);
							ctx.restore();
						}
					}
				}],
				options: {
					responsive: false,
					maintainAspectRatio: false,
					animation: {
						duration: 0 // Disable animations to prevent loading states
					},
					interaction: {
						intersect: false,
						mode: 'index'
					},
					elements: {
						point: {
							radius: 0,
							hoverRadius: 0
						},
						line: {
							borderWidth: 1.5
						}
					},
					datasets: {
						line: {
							pointRadius: 0,
							pointHoverRadius: 0
						}
					},
					scales: {
						x: {
							type: 'time',
							time: {
								unit: this.getTimeUnit(),
								displayFormats: this.getDisplayFormats(),
								tooltipFormat: 'HH:mm'
							},
							grid: {
								display: false
							},
							ticks: {
								font: {
									size: 9
								},
								maxTicksLimit: 4,
								source: 'auto'
							}
						},
						y: {
							ticks: {
								font: {
									size: 9
								},
								maxTicksLimit: 4
							},
							grid: {
								display: true,
								color: 'rgba(0,0,0,0.1)'
							}
						}
					},
					plugins: {
						legend: {
							display: false
						},
						tooltip: {
							enabled: true,
							mode: 'nearest',
							intersect: false,
							callbacks: {
								title: (context) => {
									const date = new Date(context[0].parsed.x);
									return this.formatTooltipTime(date);
								},
								label: (context) => {
									return `${context.parsed.y.toFixed(config.tooltipDecimals)}${config.tooltipSuffix}`;
								}
							},
							displayColors: false,
							backgroundColor: 'rgba(255, 255, 255, 0.9)',
							titleColor: 'black',
							bodyColor: 'black',
							borderColor: 'rgba(0, 0, 0, 0.2)',
							borderWidth: 1,
							cornerRadius: 4,
							padding: 8
						},
						zoom: {
							limits: {
								x: {min: 'original', max: 'original'},
								y: {min: 'original', max: 'original'}
							},
							pan: {
								enabled: true,
								mode: 'xy'
							},
							zoom: {
								wheel: {
									enabled: true
								},
								pinch: {
									enabled: true
								},
								mode: 'xy',
								drag: {
									enabled: false
								}
							}
						}
					}
				}
			});

			// Store the chart instance
			(this as any)[config.chartRef] = chart;

		} catch (error) {
			console.error(`Failed to create chart ${config.id}:`, error);
		}
	}

	private getTimeUnit(): 'hour' | 'day' | 'month' {
		// Map tickInterval to Chart.js time units
		if (this.tickInterval.includes('hour')) {
			return 'hour';
		} else if (this.tickInterval.includes('day')) {
			return 'day';
		} else if (this.tickInterval.includes('month')) {
			return 'month';
		}
		return 'hour'; // default
	}

	private getDisplayFormats(): any {
		// Map tickOptions to Chart.js display formats
		const formats: any = {};
		if (this.tickOptions === '%H') {
			formats.hour = 'HH';
		} else if (this.tickOptions === '%d %m %H') {
			formats.hour = 'dd MM HH';
			formats.day = 'dd MM';
		} else if (this.tickOptions === '%d %m') {
			formats.day = 'dd MM';
			formats.month = 'dd MM';
		}
		return formats;
	}

	private formatTooltipTime(date: Date): string {
		// Format time based on current tickOptions
		if (this.tickOptions === '%H') {
			return date.toLocaleTimeString('en-US', { 
				hour: '2-digit', 
				minute: '2-digit',
				hour12: false 
			});
		} else if (this.tickOptions.includes('%d %m %H')) {
			return `${date.getDate()} ${date.getMonth() + 1} ${date.getHours().toString().padStart(2, '0')}`;
		} else if (this.tickOptions === '%d %m') {
			return `${date.getDate()} ${date.getMonth() + 1}`;
		}
		return date.toLocaleTimeString('en-US', { 
			hour: '2-digit', 
			minute: '2-digit',
			hour12: false 
		});
	}


	public export(): void {
		if (this.selectedUnit && this.selectedUnit.Title.includes('O.')) {
			this.ExportRequestModel.IncludeAirFlowDiff = false;
		} else {
			this.ExportRequestModel.IncludeAirFlowDiff = true;
		}

		this.renderer.setStyle(document.body, 'cursor', 'progress');

		this.ExportRequestModel.UnitId = this.selectedUnit?.UnitId ?? null;
		this.ExportRequestModel.PositionId = this.selectedPosition?.Id ?? null;
		this.ExportRequestModel.StartDate = this.formatDate(this.startDate);
		this.ExportRequestModel.EndDate = this.formatDate(this.endDate);
		this.ExportRequestModel.MeasurementType = this.activeMeasurementType;

		this.http.post('/api/measurements/export', this.ExportRequestModel, {
			responseType: 'arraybuffer',
			headers: { 'Content-type': 'application/json' }
		}).subscribe(response => {
			const blob = new Blob([response as ArrayBuffer], { type: 'text/csv' });
			saveAs(blob, 'export.csv');
			this.renderer.setStyle(document.body, 'cursor', 'default');
		}, () => {
			this.renderer.setStyle(document.body, 'cursor', 'default');
		});
	}

	public toggleExportField(field: any): void {
		this.justLoaded[field] = !this.justLoaded[field];
		this.ExportRequestModel[field] = !this.ExportRequestModel[field];
		if (this.ExportRequestModel[field]) {
			setTimeout(() => {
				this.showGraph();
			});
		} else {
			this.showGraph();
		}
	}



	public setMeasurementType(type: number): void {
		// Update both properties to keep them in sync
		this.ExportRequestModel.MeasurementType = type;
		this.activeMeasurementType = type;
		
		// Update the graph
		this.showGraph();
	}

	public showGraph(): void {
		if (this.tempData || this.humidityData || this.airChangeData || this.airFlowDiffData) {
			this.renderGraph(this.tempData, this.humidityData, this.airChangeData, this.airFlowDiffData);
		}
	}

	public preview(): void {
		this.ExportRequestModel.UnitId = this.selectedUnit?.UnitId ?? null;
		this.ExportRequestModel.PositionId = this.selectedPosition?.Id ?? null;
		this.ExportRequestModel.StartDate = this.formatDate(this.startDate);
		this.ExportRequestModel.EndDate = this.formatDate(this.endDate);
		this.ExportRequestModel.MeasurementType = this.activeMeasurementType;

		this.http.post("/api/measurements/exportPreview", this.ExportRequestModel, {
			headers: {
				'Content-Type': 'application/json'
			}
		}).subscribe(response => {
			const data = response as {
				temperatureData: any[];
				humidityData: any[];
				airChangeData: any[];
				airFlowDiffData: any[];
				timeScale: number;
			};

			if (data.temperatureData?.length) {
				this.tempData = data.temperatureData;
				this.showTemperaturePlot = true;
			} else {
				this.tempData = [''];
				this.temperaturePlotLegend = this.plotLegendNoDataFound;
			}

			if (data.humidityData?.length) {
				this.humidityData = data.humidityData;
				this.showHumidityPlot = true;
			} else {
				this.humidityData = [''];
				this.humidityPlotLegend = this.plotLegendNoDataFound;
			}

			if (data.airChangeData?.length) {
				this.airChangeData = data.airChangeData;
				this.showAirChangePlot = true;
			} else {
				this.airChangeData = [''];
				this.airChangePlotLegend = this.plotLegendNoDataFound;
			}

			if (data.airFlowDiffData?.length) {
				this.airFlowDiffData = data.airFlowDiffData;
				this.showAirFlowDiffPlot = true;
			} else {
				this.airFlowDiffData = [''];
				this.airFlowDiffPlotLegend = this.plotLegendNoDataFound;
			}

			const period = (this.endDate.getTime() - this.startDate.getTime()) / 3600000; // hours
			switch (data.timeScale) {
				case 0:
					this.tickOptions = '%H';
					this.tickInterval = '1 hour';
					break;
				case 1:
					this.tickOptions = '%d %m %H';
					if (period < 48) {
						this.tickInterval = '6 hours';
					} else if (period < 72) {
						this.tickInterval = '10 hours';
					} else if (period < 24 * 7) {
						this.tickOptions = '%d %m';
						this.tickInterval = '1 day';
					} else if (period < 24 * 30) {
						this.tickOptions = '%d %m';
						this.tickInterval = '4 day';
					} else {
						this.tickOptions = '%d %m';
						this.tickInterval = '7 day';
					}
					break;
				case 2:
					this.tickOptions = '%d %m';
					this.tickInterval = '1 month';
					break;
			}

			this.showTemperaturePlot = true;
			this.showHumidityPlot = true;

			setTimeout(() => {
				this.renderGraph(this.tempData, this.humidityData, this.airChangeData, this.airFlowDiffData);
			}, 500);
		});
	}

	public selectedSiteChange(site: any): void {
		if (!site || site === "") {
			this.selectedSite = {
				SiteString: "- " + this.content.getContent("ExportMeasurement.ShowAllSites") + " -",
				ShowAll: true
			};
			this.selectedSiteDisplay = this.selectedSite;
		} else {
			this.selectedSite = site;
			this.selectedSiteDisplay = site;
		}
		this.selectedPosition = null;
		this.filteredLocationsTemp = this.filteredLocations;
		this.filteredLocations = [];

		if (this.selectedSite && typeof this.selectedSite.ShowAll === 'undefined') {
			for (let i = 0; i < this.locations.length; i++) {
				const location = this.locations[i];
				if (location.LocationPath && location.LocationPath.startsWith("|" + this.selectedSite.SiteString)) {
					this.filteredLocations.push(location);
				}
			}
		} else {
			this.filteredLocations = [...this.locations];
		}
	}

	public searchTextChange(searchText: any): void {
		// This method is called when the search text changes in the autocomplete
		// Handle both string and object inputs
		if (typeof searchText === 'string') {
			this.searchText = searchText;
			this.searchLoc = searchText; // Also update searchLoc for position filtering
		} else if (searchText && typeof searchText === 'object') {
			// If it's an object (selected item), extract the display text
			if (searchText.Title) {
				// This is a unit
				this.searchText = searchText.Title;
			} else if (searchText.LocationStringPath) {
				// This is a position
				this.searchLoc = searchText.LocationStringPath;
			} else if (searchText.SiteString) {
				// This is a site
				this.searchText = searchText.SiteString;
			} else {
				// Fallback - try to convert to string
				this.searchText = String(searchText);
				this.searchLoc = String(searchText);
			}
		} else {
			// Fallback for null/undefined
			this.searchText = '';
			this.searchLoc = '';
		}
	}

	public displaySiteFn = (site: any): string => {
		return site ? site.SiteString : '';
	}

	public displayUnitFn = (unit: any): string => {
		return unit ? unit.Title : '';
	}

	public displayPositionFn = (position: any): string => {
		return position ? position.LocationStringPath : '';
	}

	public selectedItemChange(item: any): void {
		if (item) {
			if (item.Title) {
				// This is a unit
				this.selectedUnit = item;
			} else if (item.LocationStringPath) {
				// This is a position
				this.selectedPosition = item;
			}
		}
	}

	public disableButton(): boolean {
		const startDate = this.startDate;
		const endDate = this.endDate;
		const selectedUnit = this.selectedUnit;
		const selectedPosition = this.selectedPosition;
		const selectedUnitAndPOsition = !!(selectedUnit || selectedPosition);
		const res = !(startDate && endDate && selectedUnitAndPOsition);

		return res;
	}

	public getFilteredUnits(): any[] {
		if (!this.searchText || typeof this.searchText !== 'string') {
			return this.units.sort((a, b) => a.Title.localeCompare(b.Title));
		}
		return this.units
			.filter(unit => unit.Title.toLowerCase().includes(this.searchText.toLowerCase()))
			.sort((a, b) => a.Title.localeCompare(b.Title));
	}

	public getFilteredLocations(): any[] {
		if (!this.searchLoc || typeof this.searchLoc !== 'string') {
			return this.filteredLocations.sort((a, b) => a.LocationString.localeCompare(b.LocationString));
		}
		return this.filteredLocations
			.filter(location => location.LocationString.toLowerCase().includes(this.searchLoc.toLowerCase()))
			.sort((a, b) => a.LocationString.localeCompare(b.LocationString));
	}

	public disablePos(): void {
		// Enable unit selection, disable position and site
		this.isDisabledUnit = false;
		this.isDisabledPosition = true;
		this.isDisabledSite = true;

		// Clear position selection
		this.selectedPosition = null;
		this.searchLoc = "";

		// Focus on unit input and open autocomplete after a delay to ensure DOM is updated
		setTimeout(() => {
			this.unitInput.nativeElement.focus();
			this.unitAutoTrigger.openPanel();
		}, 100);
	}

	public disableUnit(): void {
		// Enable position selection, disable unit and site (if not site level active)
		this.isDisabledUnit = true;
		this.isDisabledPosition = false;
		this.isDisabledSite = this.siteLevelActive ? false : true;

		// Clear unit selection
		this.selectedUnit = null;
		this.searchText = "";

		// Focus on position input and open autocomplete after a delay to ensure DOM is updated
		setTimeout(() => {
			this.positionInput.nativeElement.focus();
			this.positionAutoTrigger.openPanel();
		}, 100);
	}

	public disableUnitAndPosition(): void {
		// Enable site selection, disable unit and position
		this.isDisabledUnit = true;
		this.isDisabledPosition = true;
		this.isDisabledSite = false;

		// Clear unit and position selections
		this.selectedUnit = null;
		this.selectedPosition = null;
		this.searchText = "";
		this.searchLoc = "";

		// Focus on site input after a longer delay to ensure DOM is updated
		setTimeout(() => {
			if (this.elementRef && this.elementRef.nativeElement) {
				const siteElement = this.elementRef.nativeElement.querySelector('.autoCompSite input');
				if (siteElement) {
					siteElement.focus();
				}
			}
		}, 200);
	}

	public getPositionClass(location: { Level: number }): string {
		let locClass = this.siteLevelActive ? "siteLevelActive " : " ";
		locClass += "level-" + location.Level;
		return locClass;
	}

	public getFinalLocationOverview(): void {
		this.http.get<boolean>('/api/Tenant/HasSiteLevel').subscribe((response) => {
			this.siteLevelActive = response;
			if (this.siteLevelActive) {
				this.getLocationOverviewFlattenWithSites();
				this.getSiteList();
			} else {
				this.getLocationOverviewFlatten();
			}
		});
	}

	public getLocationOverviewFlatten(): void {
		this.http.get<any[]>("/api/location/locationOverviewFlatten").subscribe((response) => {
			this.locations = [{ NodeType: 8, Name: "" }, ...response];
			this.filteredLocations = response;
			this.getReverseLocationString();
		});
	}

	public getLocationOverviewFlattenWithSites(): void {
		this.http.get<any[]>("/api/location/LocationOverviewFlattenWithSites").subscribe((response) => {
			this.locations = response;
			this.filteredLocations = response;
			this.getReverseLocationString();
		});
	}

	public getReverseLocationString(): void {
		for (let i = 0; i < this.filteredLocations.length; i++) {
			const location = this.filteredLocations[i];
			location["LocationStringPath"] = "";
			const parts = location.LocationString.split("|");

			for (let j = parts.length - 1; j >= 0; j--) {
				if (parts[j] !== "") {
					location["LocationStringPath"] += (j > 1 ? parts[j] + " | " : parts[j]);
				}
			}
		}
	}

	public getSiteList(): void {
		this.http.get<any[]>("/api/location/GetAllSites").subscribe((response) => {
			this.sites = response;
			const allSitesEntry = {
				SiteString: "- " + this.content.getContent("ExportMeasurement.ShowAllSites") + " -",
				ShowAll: true,
			};
			this.sites.push(allSitesEntry);
			
			// Preselect "Show All Sites" by default
			this.selectedSiteChange(allSitesEntry);
		});
	}

	public goBack(): void {
		this.router.navigate(['/Main']);
	}
}

